import { RenderSkedCaption } from '@dialogueconsulting/sked-preview'
import styled from 'styled-components'

import {
  CarouselIcon,
  ReelIcon,
  VideoIcon,
  StoryIcon,
  TextIcon,
  ClockIcon,
} from 'Icons'
import { datePlaceholderForDraftPost, deMapPlatform } from 'normalizer'
import type { PostType } from 'Types'

import AvatarComponent from './Common/AvatarComponent'
import type { PlatformIconProps } from './Common/PlatformIcon'
import { StatusLabel } from './post'

export interface UpcomingPostCardProps {
  id: string
  status: string
  statusLabel?: string
  time: string
  accounts: {
    avatar: string
    name: string
    avatarBorderColor: string
  }[]
  caption: string
  platform?: PlatformIconProps['platform']
  media: {
    src?: string
    type: PostType
  }
  isUndo?: boolean
  undoClick: (postId: string, status: 'pending') => void
  isSelected?: boolean
  updatePostState: (
    reviewKey: string,
    postId: string,
    finalState: string
  ) => void
  when?: string
}

const UpcomingPostCard: React.FC<UpcomingPostCardProps> = ({
  id,
  status,
  statusLabel,
  time,
  accounts,
  caption,
  platform,
  isSelected,
  media,
  updatePostState,
}) => {
  const renderMedia = (media: { src?: string; type: PostType }) => {
    switch (media.type) {
      case 'carousel':
        return (
          <>
            <img src={media.src} alt={media.type} />
            <MediaIconWrap>
              <CarouselIcon />
            </MediaIconWrap>
          </>
        )
      case 'singleImage':
        return <img src={media.src} alt={media.type} />
      case 'video':
        return (
          <>
            <img src={media.src} alt={media.type} />
            <MediaIconWrap>
              <VideoIcon />
            </MediaIconWrap>
          </>
        )
      case 'reel':
        return (
          <>
            <img className="thin" src={media.src} alt={media.type} />
            <MediaIconWrap>
              <ReelIcon />
            </MediaIconWrap>
          </>
        )
      case 'story':
        return (
          <>
            <img className="thin" src={media.src} alt={media.type} />
            <MediaIconWrap>
              <StoryIcon />
            </MediaIconWrap>
          </>
        )
      case 'text':
        return (
          <MediaIconWrap>
            <TextIcon data-testid="text_icon" />
          </MediaIconWrap>
        )
    }
  }

  const accountLengthLimit = media.type === 'text' ? 2 : 1

  const isDisplayTime =
    status !== 'requestChange' && time !== datePlaceholderForDraftPost

  return (
    <Card
      $isSelected={isSelected}
      $isDisplayTime={isDisplayTime}
      $statusState={status}
    >
      {isSelected && (
        <SelectedIdicator
          $isDisplayTime={isDisplayTime}
          data-testid="selected_indicator"
        />
      )}
      <Row>
        {status !== 'pending' && <UnSelectedOverlay />}

        <StatusLabel
          postId={id}
          status={status}
          statusLabel={statusLabel}
          platform={platform}
          time={time}
          isMobile={false}
          isReadOnly={false}
          isDisplayTime={isDisplayTime}
          updatePostState={updatePostState}
        />

        <Col>
          {accounts.length > 0 ? (
            <AccountWrap>
              <AccountInnerWrap>
                {accounts
                  .filter((_, index) => index < accountLengthLimit)
                  .map((account) => (
                    <Account key={account.name} data-testid="account_avatar">
                      <AvatarComponentStyled
                        src={account.avatar}
                        alt={account.name}
                        $bordercolor={account.avatarBorderColor}
                      />
                      <span>{account.name}</span>
                    </Account>
                  ))}
              </AccountInnerWrap>
              <span
                data-testid="excessive_account_count"
                className="excessive_count"
              >
                {accounts.length > accountLengthLimit &&
                  `+${accounts.length - accountLengthLimit}`}
              </span>
            </AccountWrap>
          ) : (
            <Account key={accounts[0].name} data-testid="account_avatar">
              <AvatarComponentStyled
                src={accounts[0].avatar}
                alt={accounts[0].name}
                $bordercolor={accounts[0].avatarBorderColor}
              />
              <span>{accounts[0].name}</span>
            </Account>
          )}
          <Caption>
            <RenderSkedCaption
              caption={caption}
              targetPlatform={deMapPlatform(platform ?? '')}
            />
          </Caption>
        </Col>
        <RightCol>
          {isDisplayTime && (
            <TimeStyled>
              <div>
                <ClockIcon />
                {time}
              </div>
            </TimeStyled>
          )}
          <MediaWrap data-testid="media">{renderMedia(media)}</MediaWrap>
        </RightCol>
      </Row>
    </Card>
  )
}

const Card = styled.div<{
  $isSelected?: boolean
  $isDisplayTime: boolean
  $statusState: string
}>`
  min-height: ${(props) => (props.$isDisplayTime ? '173px' : '140px')};
  border-radius: 5px;
  border: 1.5px solid ${(props) => (props.$isSelected ? '#4F31FF' : '#eeeeee')};
  background: ${(props) => {
    switch (props.$statusState) {
      case 'pending':
        return '#f6f5ff'
      case 'approved':
        return '#ffffff'
      case 'requestChange':
        return '#ffffff'
      default:
        return '#ffffff'
    }
  }};

  position: relative;
  &:hover {
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.15);
  }
  margin: 0 0 0 14px;
`

const Account = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  margin-bottom: 8px;

  span {
    font-weight: 500;
  }
`

const AvatarComponentStyled = styled(AvatarComponent)<{
  $bordercolor?: string
}>`
  min-width: 35px;
  width: 35px;
  height: 35px;
  object-fit: cover;
  border: 2.5px solid ${(props) => props.$bordercolor || 'black'};
  border-radius: 50%;
  margin-right: 8px;

  img {
    border-radius: 50%;
  }
`

const Caption = styled.div`
  font-size: 12px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`

const SelectedIdicator = styled.div<{ $isDisplayTime: boolean }>`
  position: absolute;
  top: -1px;
  left: -14px;
  width: 6px;
  height: ${(props) => (props.$isDisplayTime ? '173px' : '140px')};
  border-radius: 30px;
  background: #4f31ff;
`

const Col = styled.div`
  padding: 6px 0 15px 15px;
  margin-top: 40px;
  max-width: 220px;
`

const AccountWrap = styled.div`
  display: flex;
  align-items: center;

  span.excessive_count {
    font-size: 12px;
    position: relative;
    margin-left: 12px;
    font-weight: 500;
    margin-bottom: 8px;
  }
`

const AccountInnerWrap = styled.div`
  display: flex;
  gap: 12px;
`

const RightCol = styled.div`
  padding: 10px;

  @media (max-width: 1280px) {
    margin-top: 34px;
  }
`

const MediaWrap = styled.div`
  position: relative;

  img {
    height: 120px;
    max-width: 120px;
    border-radius: 5px;
    object-fit: cover;
    margin-left: auto;

    &.thin {
      min-width: 95px;
    }
  }
`
const MediaIconWrap = styled.div`
  width: 22px;
  position: absolute;
  right: 0;
  top: 0;
  margin: 8px;
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
`

export const handleStatusStateBg = (status: string, isReadOnly: boolean) => {
  if (isReadOnly) {
    return '#4F31FF'
  }

  switch (status) {
    case 'pending':
      return '#FCD34D'
    case 'approved':
      return '#10B981'
    case 'requestChange':
      return '#FA4F38'
    default:
      return '#9CA3AF' // neutral gray for custom statuses
  }
}

export const handleStatusStateColor = (status: string) => {
  switch (status) {
    case 'pending':
      return '#333333'
    case 'approved':
      return '#FFFFFF'
    case 'requestChange':
      return '#FFFFFF'
    default:
      return '#FFFFFF' // white text for custom statuses
  }
}

const UnSelectedOverlay = styled.div`
  position: absolute;
  background: #ffffff;
  width: 100%;
  height: 100%;
  z-index: 1;
  border-radius: 5px;
  opacity: 0.4;
`

export const TimeStyled = styled.div<{ $isMobile?: boolean }>`
  > div {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #4f31ff;
    font-weight: 700;
    justify-content: flex-end;
    margin: 1px 0 14px 0;

    svg {
      width: 15px;
      height: 15px;
    }
  }
  ${(props) =>
    props.$isMobile &&
    `
    svg {
      stroke: white;
    }
    > div {
      margin: 0;
      color: white;
    }
  `}
`

export default UpcomingPostCard
